import {
  fetchLogDrain,
  selectLogDrainById,
  selectUserHasPerms,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { capitalize } from "@app/string-utils";
import { useState } from "react";
import { Button } from "./button";
import { Code } from "./code";
import { CopyTextButton } from "./copy";
import { IconEyeClosed, IconEyeOpen } from "./icons";
import { LoadingSpinner } from "./loading";
import { Tooltip } from "./tooltip";

interface SensitiveLogDrainFieldProps {
  logDrainId: string;
  envId: string;
  fieldValue: string;
  label: string;
  renderValue?: (value: string) => React.ReactNode;
}

export const SensitiveLogDrainField = ({
  logDrainId,
  envId,
  fieldValue,
  label,
  renderValue,
}: SensitiveLogDrainFieldProps) => {
  const dispatch = useDispatch();
  const [isShowing, setIsShowing] = useState(false);

  const hasPerm = useSelector((s) =>
    selectUserHasPerms(s, { scope: "sensitive", envId }),
  );

  const fetchAction = fetchLogDrain({
    id: logDrainId,
    withSensitiveExtras: true,
  });
  const loader = useLoader(fetchAction);

  const logDrain = useSelector((s) =>
    selectLogDrainById(s, { id: logDrainId }),
  );
  const hasFetchedSensitiveData = Boolean(logDrain.id && fieldValue);

  useLoaderSuccess(loader, () => {
    setIsShowing(true);
  });

  const handleToggle = () => {
    if (!hasFetchedSensitiveData && !isShowing && !loader.isLoading) {
      dispatch(fetchAction);
    } else {
      setIsShowing(!isShowing);
    }
  };

  let EyeIcon = <IconEyeOpen variant="sm" />;
  if (loader.isLoading) {
    EyeIcon = <LoadingSpinner variant="sm" />;
  } else if (isShowing) {
    EyeIcon = <IconEyeClosed variant="sm" />;
  }

  if (!hasPerm) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0 flex-shrink">
          <Code>{label}</Code>
        </div>
        <Tooltip
          text={`You do not have "${capitalize("sensitive")}" permissions for this environment (id:${envId})`}
        >
          <Button
            variant="white"
            size="sm"
            disabled
            className="px-2 py-1 flex-shrink-0"
            aria-label="Show sensitive data (requires sensitive permissions)"
          >
            {EyeIcon}
          </Button>
        </Tooltip>
      </div>
    );
  }

  let buttonAriaLabel = "Show sensitive data";
  if (loader.isLoading) {
    buttonAriaLabel = "Loading sensitive data";
  } else if (isShowing) {
    buttonAriaLabel = "Hide sensitive data";
  }

  const renderFieldContent = () => {
    if (!isShowing || !fieldValue) {
      return <Code>{label}</Code>;
    }

    return (
      <>
        {renderValue ? (
          <div className="max-w-[180px] truncate">
            {renderValue(fieldValue)}
          </div>
        ) : (
          <Tooltip text={fieldValue}>
            <div className="max-w-[180px] truncate">
              <Code>{fieldValue}</Code>
            </div>
          </Tooltip>
        )}
        <CopyTextButton text={fieldValue} />
      </>
    );
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex items-center gap-2 min-w-0 flex-shrink">
        {renderFieldContent()}
      </div>
      <Button
        variant="white"
        size="sm"
        onClick={handleToggle}
        disabled={loader.isLoading}
        className="px-2 py-1 flex-shrink-0"
        aria-label={buttonAriaLabel}
      >
        {EyeIcon}
      </Button>
    </div>
  );
};
